import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { tickerDetailService } from '../../services/tickerDetailService';
import { SectionSkeleton, SectionError } from './CompanyProfile';

interface Props { symbol: string; }

export function InstitutionalHolders({ symbol }: Props): React.ReactElement {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tickerInstitutional', symbol],
    queryFn: () => tickerDetailService.getInstitutionalHolders(symbol),
    staleTime: 4 * 60 * 60 * 1000,
    enabled: !!symbol,
  });

  const holders = data?.data ?? [];
  if (isLoading) return <SectionSkeleton />;
  if (isError) return <SectionError message="Failed to load institutional holders" />;

  return (
    <section className="p-4 border-b border-dark-border">
      <h2 className="text-sm font-semibold text-gray-300 uppercase tracking-wider mb-3">13F Institutional Holdings</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-xs text-left border-collapse">
          <thead>
            <tr className="border-b border-dark-border">
              {['Institution', 'Shares', 'Date Reported', 'Change'].map((h) => (
                <th key={h} className="py-1.5 pr-6 text-gray-400 font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {holders.map((h, i) => (
              <tr key={i} className="border-b border-dark-border/50 hover:bg-dark-surface/50">
                <td className="py-1.5 pr-6 text-gray-200">{h.holder}</td>
                <td className="py-1.5 pr-6 text-white font-numeric">{h.shares?.toLocaleString()}</td>
                <td className="py-1.5 pr-6 text-gray-400 font-numeric">{h.dateReported?.slice(0, 10)}</td>
                <td className={`py-1.5 pr-6 font-numeric font-medium ${h.change == null ? 'text-gray-400' : h.change >= 0 ? 'text-bull-blue' : 'text-bear-red'}`}>
                  {h.change == null ? '\u2014' : `${h.change >= 0 ? '+' : ''}${h.change.toLocaleString()}`}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!holders.length && <p className="text-gray-400 text-sm mt-2">No data</p>}
      </div>
    </section>
  );
}
